import { createUuid } from '@notifi/uuid/browser';
import { useCallback, useState, type SetStateAction } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import {
  isMembershipPurchaseLine as isMembershipLine,
  withMembershipSetupFees,
} from '@notifi/shared-types/membership-line';
import type {
  EditableLineItem,
  PricebookLineItem,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import { toEditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { PickedLineItem } from '@/components/organisms/LineItemPicker';
import { partPricingTreatmentForService } from '@/lib/sales/service-rollup';
import { canCarryComponents } from '@/lib/sales/service-components';
import { laborCostFields } from '@/lib/sales/service-labor';
import {
  canHoldVisibilityDecision,
  chargesRevenue,
  pricingTreatmentOf,
} from '@/lib/sales/line-economics';
import {
  allowsDynamicPricing,
  isTMOverrideEligibleType,
  resolveTMLineSubtotal,
} from '@/lib/sales/pricingMode';
import {
  configuredServiceParts,
  documentServiceParts,
  dynamicLaborBasis,
  dynamicServicePrice,
  linkedPartLine,
  materializedPartFromLine,
  roundQuantity,
} from '@/lib/sales/dynamic-service-price';
import type {
  DynamicServicePricingConfig,
  MarkupProfile,
  ServiceLaborLine,
} from '@/types/pricebook';
import type { DocumentDynamicPricingConfig } from '@/types/estimate';
import type { MembershipPlanSummary } from '@/lib/membership/format-plan-summary';

/** The line fields that say how it is taxed and where its money posts. */
export type TaxAccountingField =
  | 'taxLineType'
  | 'taxCategory'
  | 'taxCode'
  | 'glAccount'
  | 'glCogs'
  | 'glExpenseAccount'
  | 'glClass';

/**
 * Re-run the dynamic formula for a service whose own CONFIGURATION just changed.
 *
 * Recalculate the dynamic offer and place its money on the document's lines.
 *
 * Part quantity, cost and markup still come only from the service snapshot. A
 * part row can change just one input here: line placement. Toggling a part to a
 * separate line removes its snapshotted contribution from the service line;
 * toggling it back restores it. The offer total therefore never changes.
 */
function repriceDynamicService(
  items: EditableLineItem[],
  serviceIndex: number,
): EditableLineItem[] {
  const service = items[serviceIndex];
  const config = service?.dynamicPricingConfig;
  if (!service || !config?.enabled) return items;
  // THE SNAPSHOT, not the document's part lines. What the service was
  // configured to contain is its own record; the lines are the operator's to
  // edit, and a filter they repriced to $1,000 in the Materials section must
  // never turn up inside the service's price the next time anything reprices.
  //
  // A service with no snapshot falls back to its lines. Its key is required to
  // find them: matching on `parentLineKey === lineKey` with both undefined made
  // the service its OWN part — and every other unparented line on the document
  // one too — so its price came back carrying a second copy of itself. It went
  // unnoticed while the same figure was being subtracted straight back off as
  // "separately charged".
  const partLines = service.lineKey
    ? items.filter((line) => line.parentLineKey === service.lineKey)
    : [];
  const parts = documentServiceParts(config, partLines);
  // The same formula the pricebook priced the catalog service with, so the
  // recalculation lands on the number the catalog would have produced.
  const { servicePrice } = dynamicServicePrice({ config, parts });
  return items.map((line, index) =>
    index === serviceIndex
      ? { ...line, basePrice: servicePrice, memberPrice: restatedMemberPrice(line, servicePrice) }
      : line,
  );
}

/**
 * A materialized service's labor ROWS, reduced to the pair the rest of the
 * system reads.
 *
 * Term for term with what `updateLaborCostLines` writes for the FLAT editor —
 * total hours, and the blended rate over them — because it is the same
 * reduction of the same fact. Durations are MINUTES, so `1h 30m` is 1.5 hours
 * and is worth `1.5 x rate`; a service running two crews states one figure for
 * both, at the rate that reproduces what the rows are worth.
 *
 * NOTHING is written for a configuration that states no time. A switched-off
 * configuration is a leftover the flat editor now owns the hours over, and one
 * carrying no rows at all (the shape from before they existed) has no account of
 * the time to reduce — overwriting a line's stated hours with the 0 that absence
 * reduces to would brick the save on precisely the lines that had them. Rows
 * whose durations are zero ARE an account, and reduce to zero hours as they
 * should.
 */
function reducedServiceLabor(
  config: DocumentDynamicPricingConfig,
): Pick<EditableLineItem, 'estimatedHours' | 'laborCostRate'> | undefined {
  if (config.enabled !== true || !config.laborLines?.length) return undefined;
  const basis = dynamicLaborBasis(config);
  const hours = Math.max(0, basis?.hours ?? 0);
  return {
    estimatedHours: hours,
    laborCostRate: hours > 0 ? (basis?.amount ?? 0) / hours : 0,
  };
}

/**
 * THE MEMBER BASIS, RESTATED against a price this document has just calculated.
 *
 * `memberPrice` is not a second price on a catalog line, it is the NUMERATOR of
 * a rate. Both engines charge a member `amount x memberPrice / basePrice` —
 * `computeMembershipDiscount` in `lib/sales/estimate-pricing-preview`,
 * `withItemMemberBasis` in operations — so the pair is only ever read as the
 * ratio between the two, which makes `basePrice` the DENOMINATOR. Moving one
 * without the other does not change a price, it changes the discount. That is
 * the defect the estimate editor already had once, from the other direction: a
 * typed price written over `basePrice` turned catalog `120/150` (20% off) into
 * `120/200` (40% off), and one operator decision billed a member $40/unit less
 * here than through the invoice editor (SPEC-line-item-model, "Member pricing —
 * direction decided"; pinned in `dynamic-member-markup-order.test.ts`).
 *
 * Materializing moves that denominator further than any typed price can. A $900
 * service carrying $1,400 of marked-up parts comes back from the formula at
 * $1,700, and the pricebook's $720 member price left beside it reads as
 * 720/1700: the member pays $720 for $1,700 of work — 58% off a service sold at
 * 20% off — and BOTH sides of the wire agree on it, because the frozen pair is
 * exactly what the save sends and what `withItemMemberBasis` prefers over the
 * live catalog.
 *
 * The RATE is therefore what carries, which is also the answer the spec gives:
 * member pricing applies to the FINAL price, whatever the resolution settles on
 * (SPEC-line-item-model, "Member pricing — applies to the final price"). 20% off
 * a calculated $1,700 is $1,360. The formula neither side may change stays
 * exactly as it is; only what it is fed moves.
 *
 * A line with NO readable rate carries none. `basePrice` at 0 states no ratio,
 * and both engines ignore `memberPrice` while it does (each gates the branch on
 * `basePrice > 0`). Carrying the figure across a reprice would wake it up
 * against a price it was never measured against — a $120 member price suddenly
 * live on a $1,700 line. The pair parked by the toggle is what hands it back.
 *
 * A HAND-TYPED line is the case where `memberPrice` reads as an ABSOLUTE member
 * price rather than a rate — `basePrice` is its own typed price, so
 * `amount x memberPrice / basePrice` reduces to `memberPrice`, which is why
 * `writeUnitPrice` still writes `basePrice` on one. The two readings AGREE at
 * the moment of the reprice, because the absolute IS the rate times the price it
 * was typed against; only one of them survives the price moving. An $80 member
 * price typed against a $100 service is a member deal of 20% off, not a promise
 * to sell $1,700 of materialized work for $80.
 *
 * `memberDiscountPercent` is deliberately untouched: a percentage is already a
 * rate, and applies to whatever the line resolves to.
 */
function restatedMemberPrice(line: EditableLineItem, price: number): number | null {
  const memberPrice = line.memberPrice;
  if (memberPrice == null || !Number.isFinite(memberPrice)) return null;
  const base = Number(line.basePrice);
  if (!(base > 0)) return null;
  return Math.round(memberPrice * (price / base) * 100) / 100;
}

/** The part lines a service carries, in the order the document holds them. */
function servicePartLines(
  items: EditableLineItem[],
  serviceKey: string | undefined,
): EditableLineItem[] {
  return serviceKey ? items.filter((line) => line.parentLineKey === serviceKey) : [];
}

/**
 * BUILD the dynamic service the flag was only claiming, and price it.
 *
 * The one materialization on this document. Labor rows come from the hours and
 * rate the flat line already carries, the parts come from its part lines through
 * the same `configuredServiceParts` the configuration wizard writes with, and
 * the PRICE comes from `repriceDynamicService` — so the formula still has
 * exactly one implementation here and this adds no second one. Every reason it
 * has to happen at all is written out over `updatePriceStrategy` below.
 *
 * `priceStrategy` is deliberately NOT written. Two different things ask for a
 * materialization: the per-line Flat/Dynamic toggle, which IS a change of the
 * line's own strategy, and the document's `TM_OVERRIDE` switch, which changes
 * no line's strategy and is read over the top of it
 * (`resolveEffectivePriceStrategy`). Folding the flag in here would make the two
 * indistinguishable afterwards — and that distinction is exactly what decides
 * which lines the mode may hand back when it is switched off again.
 *
 * A line with an enabled configuration is left alone: it is already the thing
 * this builds, and rebuilding would overwrite the pricebook's own configuration
 * with one reconstructed from the document and re-park a `basePrice` that is a
 * calculated price rather than a flat one.
 */
function materializeDynamicService(items: EditableLineItem[], index: number): EditableLineItem[] {
  const service = items[index];
  if (!service) return items;
  // Only a SERVICE has a configuration to build. Nothing else is offered the
  // toggle, and one that reached here through another surface keeps the
  // behaviour it has always had.
  if (!isTMOverrideEligibleType(service.type)) return items;
  // Already materialized — a catalog dynamic service, or one done twice.
  if (service.dynamicPricingConfig?.enabled) return items;

  const dormant = service.dynamicPricingConfig;
  const partLines = servicePartLines(items, service.lineKey);
  const config: DocumentDynamicPricingConfig = {
    ...dormant,
    enabled: true,
    // The rows the operator will edit, and the only place a materialized
    // service keeps its time. Seeded from `estimatedHours` x `laborRate` — the
    // fields the flat editor was already collecting — so the same 2 hours at
    // $150 buys the same $300 of labor it did before, and so the wizard's Labor
    // step has a row to open on. It offers no way to ADD one, so a service
    // materialized without rows would have no way to state its hours at all.
    laborLines: dormant?.laborLines?.length
      ? dormant.laborLines
      : [
          {
            label: 'Labor',
            durationMinutes: Math.max(0, service.estimatedHours ?? 0) * 60,
            rateSource: 'FIXED',
            fixedRate: Math.max(0, service.laborRate ?? 0),
          },
        ],
    // What the service is configured to CONTAIN, from its part lines — the one
    // derivation of that list, shared with the wizard. This is the term the
    // missing configuration was costing: each part at cost x qty x (1 + markup),
    // which is the formula's `(Material Cost x Markup)` and nothing new. An
    // empty list is an answer, not a gap: a service with no parts states that it
    // has none.
    materializedParts: configuredServiceParts(dormant, partLines),
    // The WHOLE flat price description, together: `basePrice` is the pricebook's
    // price, `manualPriceOverride` is the operator's own for this document, and
    // `memberPrice` is the numerator measured against that `basePrice`. Every
    // one of them is respent by materializing — `basePrice` becomes the
    // calculated price, the override is cleared, and the member basis is
    // restated against the new price — so restoring any of them without the
    // others comes back to a line that prices, or discounts, differently from
    // the one that left.
    flatPricingBeforeDynamic: {
      basePrice: service.basePrice,
      manualPriceOverride: service.manualPriceOverride ?? null,
      memberPrice: service.memberPrice ?? null,
    },
  };

  return repriceDynamicService(
    items.map((line, i) =>
      i === index ? { ...line, dynamicPricingConfig: config, manualPriceOverride: null } : line,
    ),
    index,
  );
}

/**
 * PUT BACK the flat service the materialization spent.
 *
 * The configuration is switched off rather than deleted — its labor rows and its
 * parts snapshot are what a second materialization restores, instead of
 * rebuilding a different configuration from the same line — and the parked
 * triple is handed back whole and spent. All three moved together, so all three
 * come back together: a `basePrice` restored beside a restated `memberPrice`
 * would not restore a price, it would invent a discount.
 *
 * A service with nothing parked keeps the price it has. One that arrived
 * materialized from the catalog has never had a flat price, and inventing one
 * for it would be a figure the operator never typed.
 *
 * Like its counterpart, this writes no `priceStrategy`.
 */
function dematerializeDynamicService(items: EditableLineItem[], index: number): EditableLineItem[] {
  const service = items[index];
  const dormant = service?.dynamicPricingConfig;
  if (!service || !dormant) return items;
  const parked = dormant.flatPricingBeforeDynamic;
  const { flatPricingBeforeDynamic: _spent, ...kept } = dormant;
  return items.map((line, i) =>
    i === index
      ? {
          ...line,
          dynamicPricingConfig: { ...kept, enabled: false },
          ...(parked
            ? {
                basePrice: parked.basePrice,
                manualPriceOverride: parked.manualPriceOverride,
                memberPrice: parked.memberPrice,
              }
            : {}),
        }
      : line,
  );
}

/**
 * Is this row's price one a customer has ALREADY APPROVED?
 *
 * True of a line the New Job form carried in from an approved estimate. Its
 * `basePrice` is not a catalog figure the document may recalculate — it is the
 * number on a document the customer signed, and the only instrument that moves
 * it afterwards is a change order.
 *
 * THE T&M SWITCH IS WHY THIS EXISTS. Every other edit to such a row goes
 * through the job form's `requestEstimateLineChange` acknowledgement — its
 * quantity, its price, its strategy, its parts. `applyPricingMode` is a
 * DOCUMENT-level control, so it reached none of that guard while rewriting the
 * `basePrice` of every eligible service on the page at once. One click
 * materialized an approved $5,000 flat service — a price built from nothing on
 * the line, since a flat service states no labor rate and no cost basis — into
 * the sum of its part lines, and the job billed $2,000 (QA: PROP-000005 /
 * EST-000088 -> JOB-000108).
 *
 * Operations refuses the same change from the other side: a converted job is
 * priced under the mode its estimate was approved with, whatever the payload
 * asks for (`approvedPricingMode` in `jobs.service.ts`). Both halves state one
 * rule, so the editor cannot show a price the engine will not bill.
 */
function carriesApprovedPrice(line: EditableLineItem): boolean {
  return line.provenance?.source === 'ESTIMATE';
}

/**
 * The services a `TM_OVERRIDE` document still owes a materialization to.
 *
 * Exactly the lines `applyPricingMode('TM_OVERRIDE')` would move, minus the
 * ones it has already moved — so it is the question "is this document's mode
 * finished?", and running the mode empties it in a single pass. Written as the
 * same four guards in the same order rather than as a rule of thumb about them,
 * because a selector that disagreed with the action it triggers is either a
 * line left underquoted or an effect that never settles.
 *
 * WHY A DOCUMENT NEEDS ASKING. The mode materializes when it is switched ON,
 * and a line added afterwards has never been through it: it sits DYNAMIC with
 * no enabled configuration, which is the shape neither engine reads as
 * materialized, so both fall through to `laborRate x hours + costBasis x
 * markup` and the parts it carries contribute nothing (the head of this file
 * has the numbers). On a surface that still shows the switch, the amber banner
 * offers to fix it. The New Job form's review of an approved estimate shows
 * neither, because the switch there could clobber approved money — so that
 * draft keeps its own mode current instead, and this is what it asks.
 *
 * APPROVED LINES ARE NOT IN IT, by `carriesApprovedPrice`. They are not owed a
 * materialization; they are owed the price on the document the customer signed.
 */
export function tmOverridePendingMaterialization(items: EditableLineItem[]): EditableLineItem[] {
  return items.filter((line) => {
    if (!allowsDynamicPricing(line)) return false;
    if (statedStrategy(line) === 'DYNAMIC') return false;
    if (carriesApprovedPrice(line)) return false;
    // Already carried through the mode. `materializeDynamicService` returns such
    // a line untouched, so leaving it in would be an effect with no fixed point.
    return line.dynamicPricingConfig?.enabled !== true;
  });
}

/** A line's OWN stated strategy, however it was spelled when it was stored. */
function statedStrategy(line: EditableLineItem): string {
  return String(line.priceStrategy ?? 'FLAT')
    .trim()
    .toUpperCase();
}

/**
 * The services a `TM_OVERRIDE` document is quoting for LESS than materializing
 * them would — i.e. the documents saved before the switch materialized anything.
 *
 * WHY THIS EXISTS RATHER THAN A REPAIR. `applyPricingMode` fixes a document at
 * the moment the operator asks for T&M pricing, which leaves every estimate, job
 * and template SAVED before that still quoting the old number. Repairing those
 * automatically — on hydration, or by a backfill — is not a display change:
 * `Estimate.grandTotal` and each line's `lineSubtotal` are PERSISTED at save
 * time, the public quote page and the PDF read those stored figures verbatim
 * (`resolveEstimateLineAmounts`), and at approval the number is countersigned
 * into an immutable PDF. Moving it is changing the price of a document the
 * customer is holding, and this product already has the mechanism for that —
 * REVISE, which snapshots `previousVersions` first. That decision is the
 * operator's, so what the editor does is TELL them, and offer the repair as one
 * deliberate click through `applyPricingMode`.
 *
 * WHICH LINES QUALIFY is asked by materializing speculatively and comparing, not
 * by a rule of thumb — so the offer appears only where taking it RAISES the
 * price. Materializing is not monotonic: a catalog service that states its
 * materials as `costBasis x markupPercent` on the line rather than as part lines
 * loses that term when it materializes, because the parts snapshot it builds
 * from the document is empty. Offering the repair there would talk the operator
 * into an underquote, so the comparison excludes it — the left side is
 * `materializeDynamicService`, the right side is `resolveTMLineSubtotal`, the
 * legacy derivation the engine actually bills such a line with today. No new
 * arithmetic on either side.
 *
 * A line whose OWN strategy says DYNAMIC is skipped for the same reason
 * `applyPricingMode` skips it: the mode did not make it dynamic and cannot
 * answer for it. That shape is #2347's, and its own toggle repairs it.
 *
 * So is an APPROVED line, for the same reason again: `applyPricingMode` will
 * not move it (`carriesApprovedPrice`), so naming it here would offer a repair
 * whose button does nothing — and describe a price the customer signed as an
 * underquote this editor is entitled to correct. It is not.
 */
export function tmOverrideUnderquotedServices(items: EditableLineItem[]): EditableLineItem[] {
  return items.filter((line, index) => {
    if (!allowsDynamicPricing(line)) return false;
    if (statedStrategy(line) === 'DYNAMIC') return false;
    if (line.dynamicPricingConfig?.enabled) return false;
    if (carriesApprovedPrice(line)) return false;

    const materialized = materializeDynamicService(items, index)[index];
    if (!materialized?.dynamicPricingConfig?.enabled) return false;

    // PER UNIT on both sides. The quantity multiplies whichever price wins and
    // cannot change which one does, and asking at the line's own quantity would
    // report a quantity-0 line as fine.
    const quotedNow = resolveTMLineSubtotal({
      quantity: 1,
      basePrice: line.basePrice,
      laborRate: line.laborRate,
      costBasis: line.costBasis,
      markupPercent: line.markupPercent,
      estimatedHours: line.estimatedHours ?? undefined,
    });
    return (materialized.basePrice ?? 0) > quotedNow;
  });
}

/**
 * A part's absolute quantity after the service above it moved from `previous`
 * to `next` of itself.
 *
 * Multiplied out BEFORE it is divided, so the common case divides exactly, and
 * rounded through `roundQuantity` — the one statement of the precision a
 * quantity is stored at, shared with the conversion that writes a snapshot, so
 * the two cannot disagree about what a storable quantity is.
 */
function scalePartQuantity(quantity: number, next: number, previous: number): number {
  return Math.max(0, roundQuantity((quantity * next) / previous));
}

/**
 * A material or equipment line as a part OF a service.
 *
 * Pure, and separate from the state update, because two actions build the same
 * line: attaching a part to a service, and adding one to a dynamic service's
 * CONFIGURATION — where the same line is also what the snapshot entry is derived
 * from, so a second construction would let the price and the line disagree.
 */
function componentLineFor(
  parent: EditableLineItem,
  picked: PickedLineItem,
  parentKey: string,
): EditableLineItem {
  // Materials and equipment are costed, not catalog-priced: what they sell
  // for is their cost plus whatever markup is put on them. A part whose
  // catalog price is blank therefore starts AT cost with no markup, rather
  // than at $0 — which is not a free part, it is a part sold at a 100% loss
  // and a markup field reading -100%.
  const cost = picked.costBasis ?? 0;
  const startsAtCost = !(picked.basePrice > 0) && cost > 0;
  // Resolved before the line is built because the visibility snapshot below
  // depends on it — what a part charges decides whether a hidden flag on it
  // says anything.
  const treatment = partPricingTreatmentForService(picked, parent);
  const line = toEditableLineItem({
    id: picked.lineItemId,
    itemVersion: picked.itemVersion,
    name: picked.name,
    code: picked.code,
    // A part typed by hand is still typed by hand once it is attached to a
    // service — same reason as `addPickedLineItem` above (NOT-631).
    isCustom: picked.isCustom,
    type: picked.type,
    unit: picked.unit,
    description: picked.description,
    imageUrl: picked.imageUrl,
    imagePreviewUrl: picked.imagePreviewUrl,
    basePrice: startsAtCost ? cost : picked.basePrice,
    costBasis: picked.costBasis,
    markupPercent: startsAtCost ? 0 : picked.markupPercent,
    // The same part must bill the same way whether it is added on its own
    // or attached to a service; the catalog answers that either way, and
    // the relationship overrules it where the service says so.
    chargeable: picked.chargeable,
    // ...and the service it is being attached TO overrules the catalog:
    // a flat-priced service already charges for its parts, so one dropped
    // onto it is cost-only rather than a second charge. Decided here, as
    // the line is made, and saved with it. An explicit treatment the part
    // arrived with is left alone.
    pricingTreatment: treatment,
    // Same snapshot as the picker's expansion — a part attached to a
    // service by hand carries whatever visibility it arrived with, so long
    // as the line it becomes is one the flag can mean anything on. It cannot
    // on a MATERIAL the service's price covers: nothing prints it either way,
    // and keeping the flag would leave an answer nobody gave to be found
    // later (`canHoldVisibilityDecision`).
    hiddenFromCustomer: canHoldVisibilityDecision({ ...picked, pricingTreatment: treatment })
      ? picked.hiddenFromCustomer
      : undefined,
    markupOverride: picked.markupOverride,
    priceStrategy: picked.priceStrategy,
    taxable: picked.taxable,
    taxLineType: picked.taxLineType,
    // Where the line's money posts, and — for a `membership` category — whether
    // it is a membership at all whatever its type says. Dropped here, a
    // service-shaped membership reached the preview looking ordinary and was
    // taxed, discounted and folded into a bundle's base that the server keeps
    // it out of (`isMembershipLine`).
    taxCategory: picked.taxCategory,
    displayConfig: picked.displayConfig,
    lineKey: `line-${createUuid()}`,
    parentLineKey: parentKey,
    isOptional: picked.isOptional,
    isSelected: picked.isSelected,
  });
  line.quantity = Math.max(0, picked.quantity ?? 1);
  return line;
}

/**
 * Put a part line under its service, giving the service a key if it has none.
 *
 * Inserted directly after the parent and its existing parts, so the array order
 * matches the order it is drawn in and a later reorder has nothing to reconcile.
 */
function insertUnderParent(
  items: EditableLineItem[],
  parentIndex: number,
  parentKey: string,
  line: EditableLineItem,
): EditableLineItem[] {
  const withKey = items.map((item, i) =>
    i === parentIndex ? { ...item, lineKey: parentKey } : item,
  );
  let insertAt = parentIndex + 1;
  while (insertAt < withKey.length && withKey[insertAt]?.parentLineKey === parentKey) {
    insertAt += 1;
  }
  return [...withKey.slice(0, insertAt), line, ...withKey.slice(insertAt)];
}

/**
 * The same material/equipment can reach a bundle through more than one member
 * service. Once those descendants are flattened under the bundle they describe
 * one inventory line, not two independently editable offers. Consolidate only
 * when every pricing/selection property agrees; ordinary duplicate picks and
 * differently priced relationships must remain distinct document lines.
 */
function matchingBundlePartIndex(items: EditableLineItem[], candidate: EditableLineItem): number {
  if (!candidate.parentLineKey) return -1;
  const parent = items.find(
    (line) => !line.parentLineKey && line.lineKey === candidate.parentLineKey,
  );
  if (
    String(parent?.type ?? '')
      .trim()
      .toUpperCase() !== 'BUNDLE'
  )
    return -1;
  const type = String(candidate.type ?? '')
    .trim()
    .toUpperCase();
  if (type !== 'MATERIAL' && type !== 'EQUIPMENT') return -1;

  return items.findIndex(
    (line) =>
      line.parentLineKey === candidate.parentLineKey &&
      line.lineItemId === candidate.lineItemId &&
      String(line.type ?? '')
        .trim()
        .toUpperCase() === type &&
      line.unit === candidate.unit &&
      line.basePrice === candidate.basePrice &&
      line.costBasis === candidate.costBasis &&
      (line.manualPriceOverride ?? null) === (candidate.manualPriceOverride ?? null) &&
      (line.pricingTreatment ?? null) === (candidate.pricingTreatment ?? null) &&
      line.chargeable === candidate.chargeable &&
      (line.markupOverride ?? null) === (candidate.markupOverride ?? null) &&
      line.isOptional === candidate.isOptional &&
      line.isSelected === candidate.isSelected &&
      line.hiddenFromCustomer === candidate.hiddenFromCustomer,
  );
}

function consolidateBundleParts(items: EditableLineItem[]): EditableLineItem[] {
  return items.reduce<EditableLineItem[]>((consolidated, line) => {
    const matchingPart = matchingBundlePartIndex(consolidated, line);
    if (matchingPart < 0) return [...consolidated, line];
    return consolidated.map((existing, index) =>
      index === matchingPart
        ? { ...existing, quantity: existing.quantity + line.quantity }
        : existing,
    );
  }, []);
}

export function useEstimateLineItemState(initialLineItems: EditableLineItem[] = []) {
  const [lineItems, setStoredLineItems] = useState<EditableLineItem[]>(() =>
    withMembershipSetupFees(consolidateBundleParts(initialLineItems)),
  );
  const setLineItems = useCallback((update: SetStateAction<EditableLineItem[]>) => {
    setStoredLineItems((previous) => {
      const next = typeof update === 'function' ? update(previous) : update;
      return next === previous ? previous : withMembershipSetupFees(next);
    });
  }, []);

  const addLineItem = useCallback((item: PricebookLineItem) => {
    setLineItems((prev) => [...prev, toEditableLineItem(item)]);
  }, []);

  const addPickedLineItem = useCallback((picked: PickedLineItem) => {
    const customerChoosesMembershipBilling =
      isMembershipLine(picked) && picked.membershipCustomerChoosesBillingOption === true;
    const next = toEditableLineItem({
      id: picked.lineItemId,
      itemVersion: picked.itemVersion,
      name: picked.name,
      code: picked.code,
      // NOT-631 — the picker is the only thing that knows a line was typed by
      // hand rather than picked from the catalog, and `toEditableLineItem`
      // reads that as `isCustom === true`. Dropped here it stamped an explicit
      // `false`, which is NOT the same as "unknown": the row's
      // `item.isCustom ?? !item.code` then stopped falling back to the blank
      // code, so the Name input never rendered — leaving "Name each custom line
      // item" impossible to satisfy and the estimate unsaveable. The same flag
      // also decides where a repriced line writes (`writeUnitPrice`), so a
      // hand-typed line's FIXED member price was being scaled as a rate.
      isCustom: picked.isCustom,
      type: picked.type,
      unit: picked.unit,
      description: picked.description,
      imageUrl: picked.imageUrl,
      imagePreviewUrl: picked.imagePreviewUrl,
      basePrice: picked.basePrice,
      laborRate: picked.laborRate,
      costBasis: picked.costBasis,
      markupPercent: picked.markupPercent,
      chargeable: picked.chargeable,
      pricingTreatment: picked.pricingTreatment,
      // The service component's visibility default, snapshotted with the rest
      // of its configuration. Dropping it here is silent: the line would come
      // back visible and nothing about the totals would look wrong.
      //
      // Carried only onto a line that can hold the answer. A MATERIAL the
      // service's price already covers is not one — it never prints whatever
      // the flag says, so storing one banks a decision nobody made against the
      // day the part is ticked billed (`canHoldVisibilityDecision`).
      hiddenFromCustomer: canHoldVisibilityDecision(picked) ? picked.hiddenFromCustomer : undefined,
      markupOverride: picked.markupOverride,
      memberPrice: picked.memberPrice,
      memberDiscountPercent: picked.memberDiscountPercent,
      priceStrategy: picked.priceStrategy,
      taxable: picked.taxable,
      taxLineType: picked.taxLineType,
      // See `addComponentLineItem` — a dropped `taxCategory` hides a
      // service-shaped membership from every path that has to treat it as one.
      taxCategory: picked.taxCategory,
      displayConfig: picked.displayConfig,
      estimatedHours: picked.estimatedHours,
      laborCostRate: picked.laborCostRate,
      laborCostLines: picked.laborCostLines,
      dynamicPricingConfig: picked.dynamicPricingConfig,
      membershipPlanSummary: picked.membershipPlanSummary,
      membershipPlanId: picked.membershipPlanId,
      membershipSetupFee: picked.membershipSetupFee,
      membershipBillingOptionId: picked.membershipBillingOptionId,
      membershipCustomerChoosesBillingOption: picked.membershipCustomerChoosesBillingOption,
      lineKey: picked.lineKey,
      parentLineKey: picked.parentLineKey,
      componentDisplayMode: picked.componentDisplayMode,
      isOptional: customerChoosesMembershipBilling ? true : picked.isOptional,
      isSelected: customerChoosesMembershipBilling ? false : picked.isSelected,
      configurationName: picked.configurationName,
    });
    // `toEditableLineItem` starts every line at quantity 1, which is right
    // for an item picked on its own but wrong for one that arrives with a
    // configured quantity — a service's included parts carry theirs (10 ft of
    // copper), and dropping it silently bills one unit of each.
    next.quantity = Math.max(0, picked.quantity ?? 1);
    // NOT-628 — the sale answers whether visit #1 was already delivered.
    // `toEditableLineItem` maps catalog fields only, so this rides alongside
    // quantity rather than through it.
    if (picked.membershipDeliveredServiceIds?.length)
      next.membershipDeliveredServiceIds = picked.membershipDeliveredServiceIds;
    // The same is true for the sold home's equipment, but [] is an explicit
    // answer here: dropping it makes Operations auto-select every matching
    // unit at the address on save.
    if (Array.isArray(picked.equipmentIds)) next.equipmentIds = picked.equipmentIds;
    // A visit the membership covers arrives with an override that holds it at
    // no charge. The catalog basis is left alone: `manualPriceOverride` is this
    // document's price, `basePrice` is what the item costs, and clearing the
    // override has to give the real price back.
    if (picked.manualPriceOverride != null) {
      next.manualPriceOverride = picked.manualPriceOverride;
    }

    setLineItems((prev) => {
      const matchingPart = matchingBundlePartIndex(prev, next);
      if (matchingPart >= 0) {
        return prev.map((line, index) =>
          index === matchingPart ? { ...line, quantity: line.quantity + next.quantity } : line,
        );
      }
      const isService = String(picked.type).toUpperCase() === 'SERVICE';
      const hasAnotherService = prev.some((line) => String(line.type).toUpperCase() === 'SERVICE');
      const rule = picked.dynamicPricingConfig?.addOnDiscount;
      if (isService && hasAnotherService && rule?.enabled) {
        const requested =
          rule.discountType === 'FLAT'
            ? Math.max(rule.value, 0)
            : (next.basePrice * Math.max(rule.value, 0)) / 100;
        next.discountAmount = Math.min(next.basePrice, requested);
      }
      return [...prev, next];
    });
  }, []);

  /**
   * Attach a material or equipment line to a service already on the document.
   *
   * The part becomes an ordinary line that happens to name its parent, so it
   * prices, taxes and costs exactly as the same item would standing on its own —
   * the link only decides where it is drawn and whose card it belongs to.
   *
   * The SERVICE is untouched: a flat price is the operator's number, and a
   * materialized dynamic one was calculated in the pricebook and is carried
   * (SPEC-line-item-model). Adding a part to what a dynamic service CONTAINS is
   * the other action — `addConfiguredServicePart` below.
   *
   * Done in one update because it may have to give the parent a `lineKey`
   * first: a service typed in by hand has no key until something needs to point
   * at it, and assigning the key in a separate pass would let a second part
   * read the parent before the key landed and orphan itself.
   */
  const addComponentLineItem = useCallback((parentIndex: number, picked: PickedLineItem) => {
    setLineItems((prev) => {
      const parent = prev[parentIndex];
      if (!parent) return prev;
      const parentKey = parent.lineKey ?? `line-${createUuid()}`;
      return insertUnderParent(
        prev,
        parentIndex,
        parentKey,
        componentLineFor(parent, picked, parentKey),
      );
    });
  }, []);

  /**
   * Add a material or equipment the service was NOT configured with.
   *
   * The wizard's Parts step could only re-count the parts the catalog put in the
   * service, so a job needing one more fitting than the template assumes had
   * nowhere to say so. This is that: the operator changes what the service
   * CONTAINS, and the service reprices from it — the same direction as changing
   * a configured quantity, and the opposite of editing a part's own line, which
   * says nothing about the service (see `updateQuantity`).
   *
   * TWO records, written together:
   *
   *   the CONFIGURATION  gains an entry, which is what prices the service. Its
   *                      per-unit contribution is derived from the line below
   *                      through the same function that reads a part line when
   *                      there is no snapshot, so the figure the wizard shows
   *                      is the figure the formula charges.
   *   the DOCUMENT       gains the part's line, `included_in_service` like any
   *                      part of a service, so job costing counts what the work
   *                      actually consumes and nothing bills twice.
   *
   * A service with no snapshot yet — materialized before it existed, or built by
   * hand — materializes the parts it already has FIRST, from the lines. Writing
   * `[part]` onto it would state that the service contains only the new one and
   * drop every part it was priced with.
   *
   * A parent that is not a dynamic service has no configuration to add to, and
   * the part is simply attached (`addComponentLineItem`'s behaviour): a flat
   * service's price is the operator's number, not a calculation to re-run.
   */
  const addConfiguredServicePart = useCallback((serviceIndex: number, picked: PickedLineItem) => {
    setLineItems((prev) => {
      const service = prev[serviceIndex];
      if (!service) return prev;
      const config = service.dynamicPricingConfig;
      const parentKey = service.lineKey ?? `line-${createUuid()}`;
      const line = componentLineFor(service, picked, parentKey);
      // Read BEFORE the line is inserted. The fallback derives the snapshot from
      // the service's existing part lines, and deriving it afterwards would
      // count the new part twice — once from its line, once as the entry added
      // right below.
      const partLines = servicePartLines(prev, service.lineKey);
      // A service that cannot ACCOUNT for its parts is not repriced.
      //
      // No snapshot and no part lines is not "a service with no parts" — that is
      // `materializedParts: []`, which every materialized service now states. It
      // is a service from before either record existed, whose parts are known
      // only to the catalog that priced it. Writing `[the new part]` over that
      // silence would reprice the service to labor plus one part and throw away
      // the parts contribution its carried catalog price is made of.
      //
      // So the part is attached and the price stands, which is the rule for a
      // materialized dynamic service everywhere else. The step still lists the
      // part — it derives from the lines, and the new line is one of them.
      const unaccountedFor = !config?.materializedParts && partLines.length === 0;
      if (!config?.enabled || unaccountedFor) {
        return insertUnderParent(prev, serviceIndex, parentKey, line);
      }
      // PER SERVICE, both of them. The existing entries are converted out of the
      // document's rows where there is no snapshot, and the part just added is
      // converted out of its own new row — so the record written here states one
      // delivery's worth throughout, which is what its format means.
      const configured = configuredServiceParts(config, partLines, service.quantity);
      const nextConfig: DynamicServicePricingConfig = {
        ...config,
        materializedParts: [...configured, materializedPartFromLine(line, service.quantity)],
      };
      const withPart = insertUnderParent(prev, serviceIndex, parentKey, line).map((item, i) =>
        i === serviceIndex ? { ...item, dynamicPricingConfig: nextConfig } : item,
      );
      return repriceDynamicService(withPart, serviceIndex);
    });
  }, []);

  /**
   * Take a part back OUT of what the service contains, by its position in the
   * configuration.
   *
   * The counterpart of adding one, and the reason adding is usable: a part
   * chosen by mistake would otherwise only be zeroable, leaving a $0 row on the
   * document describing work nobody is doing.
   *
   * The part's own line goes with it, but ONLY where the target is unambiguous —
   * `linkedPartLine`, the same rule the configured quantity carries under, so
   * the two cannot pick different rows. It answers for a part typed by hand as
   * well as a catalog one: an entry derived from a hand-typed line has no item
   * id, and matching on the id alone left that row on the document after the
   * configuration had stopped naming it. The configuration change stands either
   * way, and it is what prices the service.
   */
  const removeConfiguredServicePart = useCallback((serviceIndex: number, partIndex: number) => {
    setLineItems((prev) => {
      const service = prev[serviceIndex];
      const config = service?.dynamicPricingConfig;
      if (!service || !config?.enabled) return prev;
      const partLines = servicePartLines(prev, service.lineKey);
      const configured = configuredServiceParts(config, partLines, service.quantity);
      const removed = configured[partIndex];
      if (!removed) return prev;
      const nextConfig: DynamicServicePricingConfig = {
        ...config,
        materializedParts: configured.filter((_, index) => index !== partIndex),
      };
      const droppedRowId = linkedPartLine(partLines, removed)?._rowId;
      const next = prev
        .filter((line) => line._rowId !== droppedRowId)
        .map((line) =>
          line._rowId === service._rowId ? { ...line, dynamicPricingConfig: nextConfig } : line,
        );
      return repriceDynamicService(
        next,
        next.findIndex((line) => line._rowId === service._rowId),
      );
    });
  }, []);

  const removeLineItem = useCallback((index: number) => {
    setLineItems((prev) => {
      const removed = prev[index];
      // A service's parts cannot outlive it: left behind they would render as
      // loose lines and keep billing for work no longer being done.
      const orphanKey = removed?.lineKey;
      return prev.filter(
        (line, i) => i !== index && !(orphanKey && line.parentLineKey === orphanKey),
      );
    });
  }, []);

  /**
   * Quantity, on any line — and on the parts of the line, where it has them.
   *
   * A part's quantity does NOT reprice the service carrying it. The service's
   * price was calculated in the pricebook and the document carries it; changing
   * how many filters the job uses changes what the job COSTS, and what the part
   * charges if it charges, and nothing about the quoted service.
   *
   * THE INVARIANT, in the other direction. A part's quantity is stated PER
   * parent service in the catalog and stored ABSOLUTE on the document: two
   * water heater installs that each consume two copper fittings is a line of
   * four. Operations states the same rule and enforces it at expansion —
   * `expandComponentLines` writes `component.quantity * line.quantity`
   * (estimate-application.service) — and the picker's bundle path does the same
   * arithmetic when it flattens a member service's parts. This editor was the
   * one place the two could drift: raising a service to 2 rewrote that row
   * alone, leaving its parts at their quantity-1 expansion. Price doubled,
   * because the engine multiplies the unit price by the parent's quantity;
   * cost did not, because the roll-up reads each part's own stored quantity.
   * The reported bug (NOT-633): $5,900 of price against $681 of cost, an
   * 88.46% margin on a job that earns 76.92%.
   *
   * A RATIO, not a rewrite. The parts are the operator's rows — one may have
   * been re-quantified by hand, and the document's own record of how many of it
   * a single service consumes is `part.quantity / service.quantity`. Scaling
   * preserves that ratio for every part at once, so a hand-typed 5 becomes 10
   * rather than being reset to whatever the catalog said. Rounded to the four
   * decimals the column stores, which is float noise removal and not a
   * quantization: `6 x (1/3)` must come back as 2, and part quantities are
   * legitimately fractional (feet of pipe, pounds of refrigerant).
   *
   * Going to zero takes the parts to zero with it, which is the same rule read
   * literally: none of a service consumes none of its parts.
   */
  const updateQuantity = useCallback((index: number, quantity: number) => {
    setLineItems((prev) => {
      const target = prev[index];
      if (!target) return prev;
      const next = Math.max(0, quantity);
      const previous = target.quantity;
      // Nothing to scale BY when the line was at zero — there is no ratio to
      // read out of it — and nothing to scale when the number has not moved.
      // A line with no key carries no parts by definition.
      //
      // And the parent has to still BE a parent. `canCarryComponents` is the
      // predicate `groupComponentLines` anchors on, so a line that cannot hold
      // components is already drawn as owning none; asking it here too means the
      // cascade and the renderer answer ownership the same way instead of one
      // scaling rows the other has stopped showing as its own. Unreachable from
      // the estimate editor today — the type control is offered only on a custom
      // line, and a component-carrying parent is always a catalog line — so this
      // states the rule rather than repairing a live bug.
      const cascades =
        Boolean(target.lineKey) &&
        canCarryComponents(target.type) &&
        previous > 0 &&
        next !== previous;
      return prev.map((item, i) => {
        if (i === index) return { ...item, quantity: next };
        if (!cascades || item.parentLineKey !== target.lineKey) return item;
        return { ...item, quantity: scalePartQuantity(item.quantity, next, previous) };
      });
    });
  }, []);

  /**
   * The configuration wizard's write — the ONLY way a materialized service's
   * scope is edited on the document, and therefore the only way its TIME is.
   *
   * The reduced pair goes with it. `estimatedHours` is not a second opinion
   * about the hours, it is the form every reader outside this editor consults:
   * the submit gate, the payload the save sends, and the schedule that plans
   * the work. Writing the rows alone left the two disagreeing, and the gate
   * reads the one nothing was updating — so an operator who entered the hours
   * inside the wizard watched the price move and both Save buttons stay grey,
   * reporting "Set hours for T&M service". Setting Duration on the FLAT editor
   * first and only then switching to Dynamic was the workaround, which is the
   * shape of the defect: two editors writing one fact to two places.
   *
   * SPEC-line-item-model, "Labor rows": `estimatedHours` and `laborCostRate`
   * "are their reduced form and are what costing multiplies. Both are written
   * together so they can never disagree." So both are written here, by the same
   * reduction `updateLaborCostLines` performs for the flat editor — total hours,
   * and the BLENDED rate over them, so `hours x rate` comes back to exactly what
   * the rows are worth even when a service runs two crews at two rates.
   *
   * `dynamicLaborBasis` is that reduction, and it is asked rather than repeated:
   * it prices the rows through the same `tieredLabor` the service is PRICED
   * with, so the hours recorded and the hours charged for cannot drift apart.
   *
   * Nothing here moves the price. That still comes from `repriceDynamicService`
   * below — the one function on this document allowed to run the formula.
   */
  const updateDynamicPricingConfig = useCallback(
    (index: number, config: DocumentDynamicPricingConfig) => {
      setLineItems((prev) => {
        const next = prev.map((item, i) =>
          i === index
            ? { ...item, dynamicPricingConfig: config, ...reducedServiceLabor(config) }
            : item,
        );
        return repriceDynamicService(next, index);
      });
    },
    [],
  );

  const updateDynamicMarkupProfile = useCallback(
    (index: number, target: 'MATERIAL' | 'EQUIPMENT', profile: MarkupProfile) => {
      setLineItems((prev) => {
        const service = prev[index];
        if (!service?.dynamicPricingConfig?.enabled) return prev;
        const nextConfig = {
          ...service.dynamicPricingConfig,
          ...(target === 'MATERIAL'
            ? { materialMarkupProfileId: profile.id }
            : { equipmentMarkupProfileId: profile.id }),
        };
        // ONLY the service's config. The part lines are left exactly as they
        // are: a markup profile chosen here is how the SERVICE's price is
        // calculated, and it is not real markup on the material — rewriting
        // each part's `markupPercent` and `basePrice` from it made the service
        // reach into lines the operator owns and reprice them.
        const next = prev.map((line, i) =>
          i === index ? { ...line, dynamicPricingConfig: nextConfig } : line,
        );
        return repriceDynamicService(next, index);
      });
    },
    [],
  );

  const updateName = useCallback((index: number, name: string) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, name } : item)));
  }, []);

  /**
   * Retype a hand-typed line. Writes the TYPE and nothing else, deliberately.
   *
   * It is tempting to re-derive `chargeable` here, since that is what
   * `defaultChargeableForType` does from the type at creation. It would be
   * wrong on every surface that can reach this. The estimate editor is
   * sectioned — the section decides the type and the selector is not rendered —
   * so the only callers are JobForm and JobTypeFormDrawer, and those keep every
   * line billed on purpose (neither opts into `partsStartUnbilled`) and render
   * no Chargeable tick (neither passes `updateChargeable`). Re-deriving there
   * turns a retyped MATERIAL unbilled with nothing on screen to turn it back.
   */
  const updateType = useCallback((index: number, type: string) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, type: type.toUpperCase() } : item)),
    );
  }, []);

  const updateUnit = useCallback((index: number, unit: string) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, unit } : item)));
  }, []);

  const updateBasePrice = useCallback((index: number, price: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, basePrice: Math.max(0, price) } : item)),
    );
  }, []);

  /**
   * The operator's price for THIS document.
   *
   * Writes `manualPriceOverride` and leaves `basePrice` alone: `basePrice` is
   * the pricebook's original and the only thing "revert to catalog" can put
   * back, and the operations pricing engine resolves the override first anyway.
   *
   * `null` REMOVES the override — the catalog price applies again. That is a
   * different thing from an override of 0, which is a real price a chargeable
   * line may legitimately carry.
   */
  const updateManualPriceOverride = useCallback((index: number, price: number | null) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, manualPriceOverride: price === null ? null : Math.max(0, price) }
          : item,
      ),
    );
  }, []);

  /**
   * Flat <-> Dynamic — which, going IN, MATERIALIZES the service.
   *
   * Writing the flag alone was NOT-632 and NOT-640, one defect wearing two
   * faces. `priceStrategy: 'DYNAMIC'` with no enabled configuration is a shape
   * neither engine can price: `isMaterializedDynamicService` says no, so both
   * fall through to the legacy single-line derivation, whose "material cost x
   * markup" term reads the SERVICE line's own `costBasis` and `markupPercent` —
   * the two columns the pricebook deliberately pins at 0 for a dynamic service.
   * The service's parts are meanwhile `included_in_service` and charge $0 by the
   * double-charging rule, so a Water Heater Install carrying $681 of cost and
   * $1,407 of marked-up parts went out priced at its labor alone: $300 against
   * $681 of cost, and $0.00 at zero hours. The same missing configuration is why
   * its labor COST read $0.00 — `laborCostRateOf` finds `laborCostRate` and
   * `costBasis` both zeroed and has no labor rows to reduce a rate from.
   *
   * So the toggle builds the thing the flag was only claiming, through
   * `materializeDynamicService` above: labor rows from the hours and rate the
   * line already carries, and a `materializedParts` snapshot of its part lines,
   * through the same `configuredServiceParts` the configuration wizard writes
   * with. The price then comes from `repriceDynamicService` — the one function
   * on this document allowed to run the formula — so no second implementation of
   * it exists. From that point the line IS a materialized dynamic service: FLAT on the document,
   * immutable, and edited only through "Edit configuration"
   * (SPEC-line-item-model, "A materialized dynamic service is FLAT on the
   * document"). Nothing subtracts from the service line when a part is billed,
   * and nothing here reaches into the part lines to reprice them.
   *
   * THE OPERATOR'S OWN PRICE IS CLEARED, and parked. `manualPriceOverride` is
   * where a price typed into this editor lives (SPEC-line-item-model, "Member
   * pricing"), and it outranks every strategy in `unitPriceOf` and in the
   * operations engine's `resolveUnitPrice` alike. Leaving it in place made
   * materializing a no-op on precisely the lines an operator had touched: the
   * configuration was built, the formula was run, `basePrice` was written — and
   * the line went on billing the old typed number, with the calculated price
   * displayed nowhere and charged never. That combination was not reachable
   * before this toggle materialized: a service arrives materialized only from
   * the catalog, and the price field is not drawn for one
   * (`EstimateLineItemsSection` gates the whole pricing block on
   * `!dynamicPricingConfig?.enabled`), so there was no way to type an override
   * onto it. Making it reachable is this toggle's to answer for, and the answer
   * the spec gives is that dynamic pricing "does not read the Price, Member
   * Price or Add-On Price fields... the formula is the whole answer".
   *
   * THE MEMBER BASIS IS RESTATED, for the same reason and by the same rule.
   * `memberPrice` is the numerator of a rate whose denominator is `basePrice`
   * (`amount x memberPrice / basePrice`, both engines), so a `basePrice` that
   * becomes the calculated price with the catalog's member price left beside it
   * does not keep a price, it invents a discount: $720 against a materialized
   * $1,700 is 58% off a service the pricebook sells to members at 20% off. The
   * rate is what the pricebook promised, so the rate is what carries —
   * `restatedMemberPrice` above, 20% of the new price rather than the old
   * figure. This and the override are ONE defect, not two: a line carrying
   * price fields that describe a pricing regime no longer in force.
   *
   * Nothing is destroyed. All three flat numbers — the pricebook's `basePrice`,
   * the operator's override and the member basis measured against that
   * `basePrice` — are parked in `flatPricingBeforeDynamic` on the configuration,
   * and handed straight back on the way out: "the operator's number is simply
   * dormant while the service is dynamic, and it has to still be there when they
   * switch back". The configuration is where they are parked
   * because the configuration is the only part of this that is SAVED. An
   * editor-only field is gone by the next reload, which is not "still there".
   *
   * COMING BACK is `dematerializeDynamicService` above: the configuration is
   * switched off rather than deleted — its labor rows and its parts snapshot are
   * what a second trip to Dynamic restores, instead of rebuilding a different
   * configuration from the same line — and the parked triple is spent. A service
   * with nothing parked (one that arrived materialized from the catalog) keeps
   * the price it has: the calculated number is the only price it has ever had,
   * and inventing a flat one for it would be a figure the operator never typed.
   *
   * Only the STRATEGY is this function's own. Both halves are shared with
   * `applyPricingMode` below, which asks for the same materialization on behalf
   * of the document-wide T&M switch without moving any line's own answer.
   */
  const updatePriceStrategy = useCallback((index: number, strategy: 'FLAT' | 'DYNAMIC') => {
    setLineItems((prev) => {
      if (!prev[index]) return prev;
      const moved =
        strategy === 'FLAT'
          ? dematerializeDynamicService(prev, index)
          : materializeDynamicService(prev, index);
      // The strategy is written LAST, over whichever of the two ran. They
      // deliberately leave it alone — see `materializeDynamicService` — because
      // the document-wide mode below asks for the same materialization without
      // changing any line's own answer.
      return moved.map((item, i) => (i === index ? { ...item, priceStrategy: strategy } : item));
    });
  }, []);

  /**
   * The document-wide "Time & materials" switch, applied to the LINES.
   *
   * `TM_OVERRIDE` rewrites every eligible service to DYNAMIC at pricing time
   * (`resolveEffectivePriceStrategy`, and `isTMOverrideEligibleLine` in the
   * operations engine). Until now that was the whole of it, and it left the
   * document holding the exact shape NOT-632 was: DYNAMIC with no enabled
   * configuration, which neither engine reads as a materialized service, so both
   * fell through to the legacy single-line derivation. Its "material cost x
   * markup" term reads the SERVICE line's own `costBasis` and `markupPercent` —
   * which a service carrying its parts as lines has no reason to fill in — while
   * the parts themselves are `included_in_service` and charge $0 by the
   * double-charging rule. So the parts contributed NOTHING: a $2,300 service
   * with $1,400 of marked-up parts went out at $300 of labor, and at $0.00 with
   * the hours left blank. The mode meant to price the work by time and materials
   * priced it by time alone.
   *
   * A service under this mode therefore arrives at its price the same way the
   * per-line toggle now does — MATERIALIZED, through the same two functions —
   * rather than through a derivation that cannot see its parts.
   *
   * WHY HERE, and not at pricing time. The alternative was to teach both engines
   * to pull a service's parts in while resolving the price. That is a second
   * implementation of the money question `dynamicServicePrice` already answers,
   * in two more places, which SPEC-line-item-model ("Centralisation") and
   * `docs/pricing-invariants.md` both refuse — and it would have to agree with
   * the first to the cent, forever, on both sides of the wire. Materializing on
   * the document instead means the mode produces ordinary materialized dynamic
   * services, which every reader already prices correctly and identically:
   * "a materialized dynamic service is FLAT on the document", carrying an
   * already-calculated price rather than re-deriving one. The switch is also the
   * only moment that can see the WHOLE document at once — the service and every
   * part line it carries — which is what the parts snapshot needs and what a
   * line-by-line hook cannot promise while a catalog service's parts are still
   * arriving.
   *
   * WHICH LINES MOVE, in each direction:
   *
   *   ON   every line the mode is about to rewrite — `allowsDynamicPricing`,
   *        the same gate `resolveEffectivePriceStrategy` consults — that is not
   *        already materialized and does not already say DYNAMIC itself.
   *   OFF  only the lines this switch materialized, told apart by the parked
   *        flat pricing it left behind and by their own strategy still reading
   *        FLAT. Handing them back is `dematerializeDynamicService`, the same
   *        parked triple the per-line toggle restores, so the operator's typed
   *        price, override and member basis all come back together.
   *
   * A line the operator put on Dynamic THEMSELVES is not the mode's to undo: its
   * own strategy says DYNAMIC, the mode is not what made it so, and switching
   * the document back to standard pricing must leave it exactly where they put
   * it. Same for a service that arrived materialized from the catalog — it has
   * no parked flat price because it never had a flat price.
   */
  const applyPricingMode = useCallback((pricingMode: 'STANDARD' | 'TM_OVERRIDE') => {
    setLineItems((prev) => {
      let next = prev;
      for (let index = 0; index < prev.length; index += 1) {
        const line = next[index];
        if (!line) continue;
        // Exactly the lines the mode rewrites. A hand-typed line states its own
        // price and is never dynamic here (`allowsDynamicPricing`), so it has
        // nothing to materialize and nothing to hand back.
        if (!allowsDynamicPricing(line)) continue;
        if (statedStrategy(line) === 'DYNAMIC') continue;
        // A price a customer has already approved is not the mode's to move.
        if (carriesApprovedPrice(line)) continue;
        if (pricingMode === 'TM_OVERRIDE') {
          next = materializeDynamicService(next, index);
        } else if (line.dynamicPricingConfig?.flatPricingBeforeDynamic) {
          next = dematerializeDynamicService(next, index);
        }
      }
      return next;
    });
  }, []);

  const updateLaborRate = useCallback((index: number, rate: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, laborRate: Math.max(0, rate) } : item)),
    );
  }, []);

  const updateEstimatedHours = useCallback((index: number, estimatedHours: number) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, estimatedHours: Math.max(0, estimatedHours) } : item,
      ),
    );
  }, []);

  /**
   * What an hour of this service costs us. COST only — no total moves.
   *
   * Deliberately separate from `updateLaborRate`, which is the BILLING rate and
   * does move a dynamic service's price. One setter for both would make the two
   * numbers impossible to keep apart, and it is their difference that is the
   * margin on labor.
   */
  const updateLaborCostRate = useCallback((index: number, rate: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, laborCostRate: Math.max(0, rate) } : item)),
    );
  }, []);

  /**
   * The labor rows on a FLAT service, and the two figures costing reads.
   *
   * Written together, in one update, because they are one fact stated twice:
   * the rows are what the operator edits, and `estimatedHours` /
   * `laborCostRate` are the reduced form `document-costing` multiplies. Setting
   * them separately would leave a render where the rows and the margin panel
   * disagree.
   *
   * The rate is BLENDED — total cost over total hours — through
   * `laborCostFields`, the reduction the job's line editor shares.
   */
  const updateLaborCostLines = useCallback(
    (index: number, lines: ServiceLaborLine[], hours: number, cost: number) => {
      setLineItems((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, ...laborCostFields(lines, hours, cost) } : item,
        ),
      );
    },
    [],
  );

  const updateDescription = useCallback((index: number, description: string) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, description } : item)));
  }, []);

  /**
   * Switching a service between one-price and itemized.
   *
   * This moves money in the tax column, not just pixels: a rolled-up service is
   * sold as one amount and taxed once on the bundle, while itemizing states each
   * part separately and taxes it on its own.
   */
  const updateComponentDisplayMode = useCallback(
    (index: number, componentDisplayMode: 'ITEMIZED' | 'ROLLED_UP') => {
      setLineItems((prev) =>
        prev.map((item, i) => (i === index ? { ...item, componentDisplayMode } : item)),
      );
    },
    [],
  );

  const updateEquipmentAssociation = useCallback(
    (index: number, equipmentIds: string[], equipmentLocationOverrideReason?: string) => {
      setLineItems((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, equipmentIds, equipmentLocationOverrideReason } : item,
        ),
      );
    },
    [],
  );

  /**
   * A membership's covered units and sold-home price are one answer.
   *
   * Operations authoritatively recalculates the same price on save. Keeping
   * this update atomic prevents the editor from showing the old amount beside
   * the newly selected units in the meantime.
   */
  const updateMembershipEquipmentAssociation = useCallback(
    (
      index: number,
      equipmentIds: string[],
      equipmentLocationOverrideReason: string | undefined,
      basePrice: number,
      membershipPlanSummary: MembershipPlanSummary,
    ) => {
      setLineItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                equipmentIds,
                equipmentLocationOverrideReason,
                basePrice: Math.max(0, basePrice),
                manualPriceOverride: null,
                membershipPlanSummary,
              }
            : item,
        ),
      );
    },
    [],
  );

  const updateMarkupPercent = useCallback((index: number, markupPercent: number) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        // Allow a negative margin (price below cost) down to -100, matching the
        // back-computed markup in the editor and the backend's markup ≥ -100
        // rule (NOT-96). Clamping to 0 here made a price<cost field disagree
        // with the stored value. (NOT-241)
        i === index ? { ...item, markupPercent: Math.max(-100, markupPercent) } : item,
      ),
    );
  }, []);

  const updateCostBasis = useCallback((index: number, costBasis: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, costBasis: Math.max(0, costBasis) } : item)),
    );
  }, []);

  const updateCostSource = useCallback(
    (index: number, supplier: { id: string; name: string } | null) => {
      setLineItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? supplier
              ? {
                  ...item,
                  costSource: 'SUPPLIER' as const,
                  costSupplierId: supplier.id,
                  costSupplierName: supplier.name,
                }
              : {
                  ...item,
                  costSource: 'DIRECT' as const,
                  costSupplierId: undefined,
                  costSupplierName: undefined,
                }
            : item,
        ),
      );
    },
    [],
  );

  const toggleOptional = useCallback((index: number) => {
    setLineItems((prev) => {
      const target = prev[index];
      const makesMembershipOptional =
        Boolean(target) &&
        isMembershipLine(target) &&
        target?.isOptional !== true &&
        (Boolean(target?.parentLineKey) || chargesRevenue(target));

      return prev.map((item, i) => {
        // Operations accepts one prospective membership per estimate. Choosing
        // another one replaces the previous offer instead of leaving the form
        // in a state the next save must reject.
        if (i !== index && makesMembershipOptional && isMembershipLine(item) && item.isOptional) {
          return {
            ...item,
            isOptional: false,
            isSelected: true,
            // Only one membership may be offered for the customer to decide.
            // Promoting another membership to that slot turns the previous
            // one into an office-selected line, so its delegation flag must
            // move with the optional state instead of contradicting it.
            membershipCustomerChoosesBillingOption: false,
          };
        }
        if (i !== index) return item;
        if (isMembershipLine(item) && item.membershipCustomerChoosesBillingOption === true) {
          return { ...item, isOptional: true, isSelected: false };
        }
        if (!item.parentLineKey && !chargesRevenue(item)) {
          return { ...item, isOptional: false, isSelected: true };
        }
        const next = !item.isOptional;
        return { ...item, isOptional: next, isSelected: !next };
      });
    });
  }, []);

  const toggleSelected = useCallback((index: number) => {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (isMembershipLine(item) && item.membershipCustomerChoosesBillingOption === true) {
          return { ...item, isOptional: true, isSelected: false };
        }
        // The SAME guard `toggleOptional` applies through the other door. A
        // A standalone non-chargeable part is already included in required
        // work and cannot be declined. Membership lines are intentionally not
        // special-cased: the estimate author may offer one as an optional
        // add-on for the customer to accept with the work.
        if (!item.parentLineKey && !chargesRevenue(item)) {
          return { ...item, isOptional: false, isSelected: true };
        }
        return { ...item, isSelected: !item.isSelected };
      }),
    );
  }, []);

  const updateTaxable = useCallback((index: number, taxable: boolean) => {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        // The two fields answer one question and must never contradict, which
        // is the invariant the pricebook form already keeps.
        //
        // Not merely cosmetic. A single line's own tax is decided by `taxable`
        // alone, so a stray `labor` on an untaxed line looks harmless — but a
        // ROLLED_UP service is taxed as ONE bundle, and the engine resolves
        // that bundle's treatment from the SERVICE member's `taxLineType`
        // without re-reading its `taxable` (see `resolveBundleTaxLineType`).
        // An untaxed service still claiming `labor` therefore taxes the whole
        // bundle, parts included, where `exempt` would have zero-rated it.
        //
        // Turning tax OFF states `exempt` and remembers what the line said, so
        // turning it back ON gives that answer back.
        //
        // Re-deriving instead would be a silent repricing: a SERVICE the
        // operator deliberately classified as `freight` derives back to
        // `labor`, which is a different tax treatment they never re-picked.
        // Only a line that stated nothing returns to null, where deriving IS
        // the answer it had.
        const wasExempt = item.taxLineType?.trim().toLowerCase() === 'exempt';

        if (!taxable) {
          return {
            ...item,
            taxable,
            taxLineType: 'exempt',
            // Guard the round trip: unticking an already-exempt line must not
            // overwrite the memory with `exempt` and make the restore a no-op.
            taxLineTypeBeforeExempt: wasExempt
              ? item.taxLineTypeBeforeExempt
              : (item.taxLineType ?? null),
          };
        }

        return {
          ...item,
          taxable,
          taxLineType: wasExempt ? (item.taxLineTypeBeforeExempt ?? null) : item.taxLineType,
          taxLineTypeBeforeExempt: undefined,
        };
      }),
    );
  }, []);

  /**
   * How this line is classified for tax, and where its money posts.
   *
   * A patch rather than a setter per field: the four GL accounts and the three
   * tax fields are one thought the operator is having about one line, and seven
   * near-identical callbacks would only be seven chances for one of them to
   * write to the wrong index.
   *
   * Empty strings are stored as null. A cleared input and a field never touched
   * are the same state — "this line says nothing about its account" — and the
   * save payload only omits blanks, so keeping `''` here would show a cleared
   * field as set until the next reload.
   */
  const updateTaxAccounting = useCallback(
    (index: number, patch: Partial<Record<TaxAccountingField, string | null>>) => {
      setLineItems((prev) =>
        prev.map((item, i) => {
          if (i !== index) return item;
          const normalized = Object.fromEntries(
            Object.entries(patch).map(([key, raw]) => [key, raw?.trim() ? raw.trim() : null]),
          );
          return { ...item, ...normalized };
        }),
      );
    },
    [],
  );

  /**
   * What a member pays for this line.
   *
   * Written as a patch of BOTH fields for the same reason the control offers
   * one mode: a fixed member price and a member discount % cannot both apply,
   * and a setter that touched only one would let the pair drift into a state
   * where the engine picks a winner the operator never chose.
   */
  /**
   * Point a line at the catalog item it just became.
   *
   * Promoting a hand-typed line creates a real pricebook row, so the line stops
   * being hand-typed: it takes the new item's id and code and is no longer
   * MANUAL. Leaving it custom would keep a document line whose `lineItemId`
   * resolves to nothing while the item it describes now exists — and the next
   * save would still record it as never having been in the catalog.
   *
   * Nothing else is touched. The price, cost and classification on the line
   * are already what the item was built FROM, and re-reading them off the new
   * catalog row would only introduce a way for the two to disagree.
   *
   * Keyed by `_rowId`, never by position. The request is in flight while the
   * list stays sortable and removable, so an index captured when the operator
   * clicked can address a different row by the time the item comes back — and
   * relinking the wrong line would overwrite its `lineItemId` and stop it being
   * the catalog item it actually is.
   */
  const linkLineToCatalogItem = useCallback(
    (rowId: string, item: { id: string; code: string; itemVersion?: number }) => {
      setLineItems((prev) =>
        prev.map((line) =>
          line._rowId === rowId
            ? {
                ...line,
                lineItemId: item.id,
                code: item.code,
                itemVersion: item.itemVersion ?? 1,
                isCustom: false,
              }
            : line,
        ),
      );
    },
    [],
  );

  const updateMemberPricing = useCallback(
    (
      index: number,
      patch: { memberPrice?: number | null; memberDiscountPercent?: number | null },
    ) => {
      setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    },
    [],
  );

  /**
   * The operator's answer to "is this line billed?", for THIS document.
   *
   * Both fields, because `pricingTreatment` is the one that decides and it
   * OUTRANKS `chargeable` (see `pricingTreatmentOf`). A part that came onto the
   * estimate through a flat-priced service carries `included_in_service`, so
   * writing only the catalog flag would leave the line contributing $0 while
   * the checkbox claimed otherwise — the treatment is exactly what has to be
   * overridden for the price to reach the total.
   *
   * The treatment is DERIVED from the flag through the canonical resolver
   * rather than spelled out here, so the two can never disagree about which
   * boolean means which treatment.
   *
   * For a materialized dynamic service, the parent is then repriced from its
   * snapshot. That does not let the child row rewrite the configured cost or
   * markup; it only moves the already-calculated amount between lines.
   *
   * VISIBILITY IS NOT TOUCHED, and that is the whole of the rule. This once
   * forced a material visible whenever it was ticked billed, to stop a hidden
   * flag inherited from the pricebook turning a new charge into money the
   * customer's page never explains. It was a guess made too late: it could not
   * tell that inherited default from an operator who had reached for the eye
   * and meant it, so it overwrote both, and hiding a charged material survived
   * only until the box was toggled off and on again.
   *
   * The default is now dropped where it is INHERITED instead: the picker does
   * not snapshot the catalog's `showInDetail` onto a part nothing prints
   * (`canHoldVisibilityDecision`). Once, as the line is created — and NOT again
   * on load, because a flag off a saved estimate is no longer an inheritance,
   * and shedding it there destroyed the operator's hide across a save. So a
   * hidden flag that reaches this function is somebody's answer, and
   * chargeability is not a question about it.
   */
  const updateChargeable = useCallback((index: number, chargeable: boolean) => {
    setLineItems((prev) => {
      const parentKey = prev[index]?.parentLineKey;
      const next = prev.map((item, i) => {
        if (i !== index) return item;
        const updated = {
          ...item,
          chargeable,
          pricingTreatment: pricingTreatmentOf({ chargeable }),
        };
        const canOfferLine = Boolean(updated.parentLineKey) || chargesRevenue(updated);
        if (!canOfferLine) {
          return {
            ...updated,
            optionStateBeforeUnbilled:
              chargesRevenue(item) && item.isOptional
                ? { isOptional: item.isOptional, isSelected: item.isSelected }
                : item.optionStateBeforeUnbilled,
            isOptional: false,
            isSelected: true,
          };
        }
        return updated.optionStateBeforeUnbilled
          ? {
              ...updated,
              ...updated.optionStateBeforeUnbilled,
              isSelected: updated.optionStateBeforeUnbilled.isOptional
                ? updated.optionStateBeforeUnbilled.isSelected
                : true,
              optionStateBeforeUnbilled: undefined,
            }
          : updated;
      });
      if (!parentKey) return next;
      const parentIndex = next.findIndex((item) => item.lineKey === parentKey);
      return parentIndex >= 0 ? repriceDynamicService(next, parentIndex) : next;
    });
  }, []);

  // Customer view only: the line keeps its cost and its contribution to the
  // service that carries it, so nothing about the money moves with this.
  const updateHiddenFromCustomer = useCallback((index: number, hidden: boolean) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, hiddenFromCustomer: hidden } : item)),
    );
  }, []);

  const updateServiceCallCredit = useCallback((index: number, credit: boolean) => {
    setLineItems((items) =>
      items.map((item, i) => (i === index ? { ...item, isServiceCallFee: credit } : item)),
    );
  }, []);

  const reorderLineItems = useCallback((oldIndex: number, newIndex: number) => {
    setLineItems((items) => arrayMove(items, oldIndex, newIndex));
  }, []);

  return {
    lineItems,
    setLineItems,
    addLineItem,
    addPickedLineItem,
    addComponentLineItem,
    addConfiguredServicePart,
    removeConfiguredServicePart,
    removeLineItem,
    updateQuantity,
    updateName,
    updateType,
    updateUnit,
    updateBasePrice,
    updateManualPriceOverride,
    updatePriceStrategy,
    applyPricingMode,
    updateLaborCostRate,
    updateLaborCostLines,
    updateLaborRate,
    updateEstimatedHours,
    updateDynamicPricingConfig,
    updateDynamicMarkupProfile,
    updateDescription,
    updateEquipmentAssociation,
    updateMembershipEquipmentAssociation,
    updateComponentDisplayMode,
    updateMarkupPercent,
    updateCostBasis,
    updateCostSource,
    updateTaxable,
    updateTaxAccounting,
    updateMemberPricing,
    linkLineToCatalogItem,
    updateChargeable,
    updateHiddenFromCustomer,
    updateServiceCallCredit,
    toggleOptional,
    toggleSelected,
    reorderLineItems,
  };
}
